import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CheckCircle, 
  XCircle, 
  Factory, 
  Hand, 
  Droplets,
  Flame,
  Wind,
  Clock,
  Award,
  ArrowRight,
  AlertTriangle,
  Eye,
  Download
} from 'lucide-react';
import BrochureDownloadModal from '../components/BrochureDownloadModal';

const WhyFactoryLaminated = () => {
  const [brochureModalOpen, setBrochureModalOpen] = useState(false);
  const [activeComparison, setActiveComparison] = useState('process');

  const comparisons = {
    process: {
      title: 'Application Process',
      manual: {
        title: 'Manual Pasting',
        image: '/manual-pasting-process.png',
        steps: [
          'Carpenter applies water-based glue with brush/roller',
          'Glue spread is uneven - thick in some areas, thin in others',
          'Air bubbles get trapped during hand pressing',
          'Heavy weights placed for 24-48 hours curing',
          'Final result depends entirely on worker skill'
        ],
        result: 'Inconsistent bond with visible imperfections'
      },
      pur: {
        title: 'CriLam PUR Machine',
        image: '/pur-machine-process.png',
        steps: [
          'Board enters precision PUR lamination machine',
          'Computer-controlled adhesive application - exactly 0.1mm thick',
          'Laminate bonded under controlled pressure and temperature',
          'Instant chemical cure - ready in seconds',
          'Every board identical - zero human error'
        ],
        result: 'Perfect bond every single time'
      }
    },
    surface: {
      title: 'Surface Finish',
      manual: {
        title: 'Manual Pasting Result',
        image: '/manual-surface-bubbles.png',
        issues: [
          'Visible air bubbles under laminate',
          'Wavy, uneven surface texture',
          'Glue seepage at edges',
          'Inconsistent gloss levels',
          'Touch reveals imperfections'
        ],
        verdict: 'Premium laminate looks cheap'
      },
      pur: {
        title: 'CriLam PUR Result',
        image: '/pur-surface-smooth.png',
        issues: [
          'Mirror-smooth surface finish',
          'Zero bubbles - guaranteed',
          'Clean, sealed edges',
          'Uniform gloss across entire surface',
          'Glass-like touch quality'
        ],
        verdict: 'European quality finish'
      }
    },
    edges: {
      title: 'Edge Bonding',
      manual: {
        title: 'Manual Edge Result',
        image: '/manual-edge-lifting.png',
        timeline: [
          '0-6 months: Looks fine initially',
          '6-12 months: Slight lifting begins at corners',
          '1-2 years: Edges visibly peeling',
          '2-3 years: Major delamination',
          '3+ years: Complete failure, replacement needed'
        ],
        verdict: 'Edges are first to fail'
      },
      pur: {
        title: 'CriLam PUR Edge',
        image: '/pur-edge-sealed.png',
        timeline: [
          '0-5 years: Perfect condition',
          '5-10 years: Still perfect, no change',
          '10-15 years: Edges intact, no lifting',
          '15+ years: Still performing like new',
          'Lifetime: Permanent chemical bond'
        ],
        verdict: '10-year written guarantee'
      }
    },
    heat: {
      title: 'Heat Resistance (Near Gas Stove)',
      manual: {
        title: 'Manual Pasting + Heat',
        image: '/manual-heat-failure.png',
        effects: [
          'Water-based glue softens at 50-60°C',
          'Cooking heat reaches 80-100°C near burner',
          'Laminate starts bubbling within months',
          'Edges lift where heat is highest',
          'Kitchen cabinets fail first near stove'
        ],
        verdict: '"Gas ke paas laminate uth gaya"'
      },
      pur: {
        title: 'CriLam PUR + Heat',
        image: '/pur-heat-resistant.png',
        effects: [
          'PUR withstands up to 150°C',
          'Chemical bond does not soften with heat',
          'No bubbling even directly above burner',
          'Edges stay sealed despite steam',
          'Kitchen remains perfect after years of cooking'
        ],
        verdict: 'Perfect for Indian kitchens'
      }
    },
    water: {
      title: 'Water/Moisture Resistance',
      manual: {
        title: 'Manual Pasting + Water',
        image: '/manual-water-damage.png',
        effects: [
          'Water-based glue dissolves in water',
          'Bathroom humidity weakens bond',
          'Sink splashes cause swelling',
          'Monsoon humidity accelerates failure',
          'Board starts rotting from inside'
        ],
        verdict: '"Bathroom mein 6 mahine bhi nahi chala"'
      },
      pur: {
        title: 'CriLam PUR + Water',
        image: '/pur-water-resistant.png',
        effects: [
          'PUR is 100% waterproof',
          'Bathroom installation - no problem',
          'Daily water splashes - no effect',
          'Coastal salt air - completely resistant',
          'Board protected for lifetime'
        ],
        verdict: 'Bathroom doors that last forever'
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]" data-testid="why-factory-laminated-page">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#004534]/30 to-transparent" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              VISUAL COMPARISON
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 font-['Outfit']">
              Why Factory-Laminated?
            </h1>
            <p className="text-xl text-[#D4AF37] font-medium mb-4">
              See the Difference with Your Own Eyes
            </p>
            <p className="text-lg text-white/70 max-w-2xl mb-8">
              Visual comparison showing exactly why manual pasting fails and how CriLam PUR-Bond™ technology 
              delivers permanent, flawless results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Comparison Selector */}
      <section className="py-8 bg-white border-b sticky top-24 z-30">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="flex flex-wrap gap-2 justify-center">
            {Object.entries(comparisons).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActiveComparison(key)}
                className={`px-6 py-3 text-sm font-medium transition-all ${
                  activeComparison === key
                    ? 'bg-[#004534] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid={`comparison-tab-${key}`}
              >
                {value.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Active Comparison */}
      <section className="py-16 lg:py-24">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            key={activeComparison}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#004534] mb-12 font-['Outfit']">
              {comparisons[activeComparison].title}
            </h2>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* Manual Side */}
              <div className="bg-red-50 border-2 border-red-200 rounded-2xl overflow-hidden">
                <div className="bg-red-500 text-white p-4 flex items-center gap-3">
                  <Hand className="h-6 w-6" />
                  <span className="font-bold text-lg">{comparisons[activeComparison].manual.title}</span>
                  <XCircle className="h-5 w-5 ml-auto" />
                </div>
                
                <div className="p-6">
                  {/* Image placeholder */}
                  <div className="bg-red-100 h-48 rounded-lg mb-6 flex items-center justify-center">
                    <div className="text-center">
                      <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-2" />
                      <p className="text-red-600 font-medium">Manual Pasting Problems</p>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {(comparisons[activeComparison].manual.steps || 
                      comparisons[activeComparison].manual.issues ||
                      comparisons[activeComparison].manual.timeline ||
                      comparisons[activeComparison].manual.effects).map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-red-100 p-4 rounded-lg">
                    <p className="text-red-700 font-semibold text-center">
                      {comparisons[activeComparison].manual.result || 
                       comparisons[activeComparison].manual.verdict}
                    </p>
                  </div>
                </div>
              </div>

              {/* PUR Side */}
              <div className="bg-green-50 border-2 border-green-200 rounded-2xl overflow-hidden">
                <div className="bg-[#004534] text-white p-4 flex items-center gap-3">
                  <Factory className="h-6 w-6" />
                  <span className="font-bold text-lg">{comparisons[activeComparison].pur.title}</span>
                  <CheckCircle className="h-5 w-5 ml-auto text-[#D4AF37]" />
                </div>
                
                <div className="p-6">
                  {/* Image placeholder */}
                  <div className="bg-green-100 h-48 rounded-lg mb-6 flex items-center justify-center">
                    <div className="text-center">
                      <Award className="h-12 w-12 text-green-600 mx-auto mb-2" />
                      <p className="text-green-700 font-medium">CriLam PUR Excellence</p>
                    </div>
                  </div>

                  <ul className="space-y-3 mb-6">
                    {(comparisons[activeComparison].pur.steps || 
                      comparisons[activeComparison].pur.issues ||
                      comparisons[activeComparison].pur.timeline ||
                      comparisons[activeComparison].pur.effects).map((item, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="bg-[#004534] p-4 rounded-lg">
                    <p className="text-[#D4AF37] font-semibold text-center">
                      {comparisons[activeComparison].pur.result || 
                       comparisons[activeComparison].pur.verdict}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Video Demo Section */}
      <section className="py-20 lg:py-28 bg-[#004534] relative">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="label-mono text-[#D4AF37] mb-4">VIDEO DEMONSTRATION</p>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']">
              Hot Water Test: Water-Based Glue vs PUR
            </h2>
            <p className="text-lg text-white/70 max-w-2xl mx-auto">
              Watch what happens when manually-pasted laminate meets hot water vs CriLam PUR-bonded laminate
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Manual Test */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/[0.05] border border-white/[0.1] rounded-2xl p-6"
            >
              <div className="aspect-video bg-red-900/30 rounded-lg mb-4 flex items-center justify-center">
                <div className="text-center">
                  <Droplets className="h-16 w-16 text-red-400 mx-auto mb-2" />
                  <p className="text-red-300 font-medium">Water-Based Glue + Hot Water</p>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Manual Pasting Test</h3>
              <p className="text-white/70 text-sm mb-4">
                Laminate submerged in hot water for 2 minutes. Watch the glue dissolve and edges lift immediately.
              </p>
              <div className="flex items-center gap-2 text-red-400">
                <XCircle className="h-5 w-5" />
                <span className="font-medium">Result: Complete Failure</span>
              </div>
            </motion.div>

            {/* PUR Test */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-white/[0.05] border border-white/[0.1] rounded-2xl p-6"
            >
              <div className="aspect-video bg-green-900/30 rounded-lg mb-4 flex items-center justify-center">
                <div className="text-center">
                  <Droplets className="h-16 w-16 text-green-400 mx-auto mb-2" />
                  <p className="text-green-300 font-medium">CriLam PUR + Hot Water</p>
                </div>
              </div>
              <h3 className="text-xl font-bold text-white mb-2">CriLam PUR Test</h3>
              <p className="text-white/70 text-sm mb-4">
                Same test - laminate in hot water for 2 minutes. PUR bond remains completely intact.
              </p>
              <div className="flex items-center gap-2 text-[#D4AF37]">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">Result: Perfect Bond</span>
              </div>
            </motion.div>
          </div>

          <div className="text-center mt-12">
            <p className="text-white/60 text-sm mb-4">
              This video is perfect for WhatsApp marketing to customers
            </p>
            <button
              onClick={() => setBrochureModalOpen(true)}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors"
            >
              <Download className="h-5 w-5" />
              Download Full Video
            </button>
          </div>
        </div>
      </section>

      {/* Edge Pull Test */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="label-mono text-[#D4AF37] mb-4">STRENGTH TEST</p>
            <h2 className="text-3xl md:text-4xl font-bold text-[#004534] mb-6 font-['Outfit']">
              Edge Pull Strength Comparison
            </h2>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="text-center p-8 bg-red-50 rounded-2xl border border-red-200"
              >
                <div className="text-6xl font-bold text-red-500 mb-4">2-3 kg</div>
                <h3 className="text-xl font-bold text-red-700 mb-2">Manual Pasting Edge Strength</h3>
                <p className="text-red-600">
                  Easily peels off with minimal force. Normal daily use causes lifting.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="text-center p-8 bg-green-50 rounded-2xl border border-green-200"
              >
                <div className="text-6xl font-bold text-[#004534] mb-4">50+ kg</div>
                <h3 className="text-xl font-bold text-[#004534] mb-2">CriLam PUR Edge Strength</h3>
                <p className="text-green-700">
                  Chemical bond that cannot be separated. Board will break before edge lifts.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 lg:py-28 bg-[#0A0D0C] relative overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.p 
              className="label-mono text-[#D4AF37] mb-4"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Make the Right Choice
            </motion.p>
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-6 font-['Outfit']"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Don&apos;t Let Manual Pasting Ruin Your Investment
            </motion.h2>
            <motion.p 
              className="text-lg text-white/70 mb-10"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Choose CriLam PUR-bonded products and get factory-laminated quality with 10-year guarantee
            </motion.p>
            
            <motion.div 
              className="flex flex-wrap justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Link to="/contact">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors flex items-center gap-2">
                  Contact Us
                  <ArrowRight className="h-4 w-4" />
                </button>
              </Link>
              <Link to="/ask-your-carpenter">
                <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">
                  Ask Your Carpenter Checklist
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Brochure Modal */}
      <BrochureDownloadModal
        isOpen={brochureModalOpen}
        onClose={() => setBrochureModalOpen(false)}
      />
    </div>
  );
};

export default WhyFactoryLaminated;
